import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  PrimaryButton,
  SingleSelect,
  Text,
  PhoneWithIsdInput,
} from "widgets";
import { useFetchCustomerInfo } from "store/backend/assets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { getDefaultCountry } from "constants/countryConfig";
import { DEFAULT_ISD_CODE, isValidPhoneForIsd } from "constants/phoneConfig";
import { ERROR_MESSAGES } from "constants/errorMessages";
import { buildCustomerOrdersPath } from "constants/omsRegionConfig";

import { BUSINESS_VERTICAL_IDS } from "constants/constants";
import Loader from "shared/Loader/Loader";
import MultipleUsers from "components/MultipleUsers/MultipleUsers";

import styles from "./HomePage.module.scss";

const selectedCountry = getDefaultCountry();

const isSelfLookup = (customerPhone, cashierPhone) => {
  if (!customerPhone || !cashierPhone) {
    return false;
  }

  return customerPhone.replace(/\D/g, "") === cashierPhone.replace(/\D/g, "");
};

const handleClickViewCustomerOrders = (
  phoneNumber,
  navigate,
  openSnackbar,
  selectedBusinessVertical,
  fetchCustomerInfo,
  setShowMultipleUsersDialog,
  setMultipleUsersList
) => {
  fetchCustomerInfo(
    {
      phoneNumber,
      isdCode: selectedCountry.isdCode,
      countryId: selectedCountry.countryId,
      countryCode: selectedCountry.countryCode,
      selectedBusinessVertical,
    },
    {
      onSuccess: (customerDetails) => {
        if (!Array.isArray(customerDetails)) {
          openSnackbar(ERROR_MESSAGES.UNEXPECTED_RESPONSE, "error");
          return;
        }
        if (customerDetails.length > 1) {
          setShowMultipleUsersDialog(true);
          setMultipleUsersList(customerDetails);
          return;
        }
        if (customerDetails.length === 1) {
          const customerId = customerDetails[0].uamId;
          navigate(
            buildCustomerOrdersPath({
              customerId,
              countryId: selectedCountry.countryId,
              isdCode: selectedCountry.isdCode,
            }),
          );
          return;
        }
        openSnackbar(ERROR_MESSAGES.CUSTOMER_NOT_FOUND, "error");
      },
      onError: (error) => {
        openSnackbar(
          error.message || ERROR_MESSAGES.CUSTOMER_FETCH_FAILED,
          "error"
        );
      },
    }
  );
};

const handleChangeBusinessVertical = (event, setSelectedBusinessVertical) => {
  const selectedBusinessVerticalId = event.target.value;
  setSelectedBusinessVertical(selectedBusinessVerticalId);
};

const canViewCustomerOrders = (
  phoneNumber,
  selectedBusinessVertical,
  cashierPhoneNumber
) => {
  if (isSelfLookup(phoneNumber, cashierPhoneNumber)) {
    return false;
  }
  const allowedVerticalValues = BUSINESS_VERTICAL_IDS.map((item) =>
    String(item.value),
  );
  return (
    isValidPhoneForIsd(phoneNumber, DEFAULT_ISD_CODE) &&
    allowedVerticalValues.includes(String(selectedBusinessVertical))
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [cashierPhoneNumber] = useState(
    localStorage.getItem("cashierPhoneNumber")
  );
  const [showMultipleUsersDialog, setShowMultipleUsersDialog] = useState(false);
  const [multipleUsersList, setMultipleUsersList] = useState([]);

  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");

  const { mutate: fetchCustomerInfo, isPending: isCustomerInfoLoading } =
    useFetchCustomerInfo();

  if (isCustomerInfoLoading) {
    return <Loader />;
  }

  const handleViewCustomerOrders = () => {
    if (isSelfLookup(phoneNumber, cashierPhoneNumber)) {
      openSnackbar(ERROR_MESSAGES.SELF_LOOKUP_BLOCKED, "error");
      return;
    }
    handleClickViewCustomerOrders(
      phoneNumber,
      navigate,
      openSnackbar,
      selectedBusinessVertical,
      fetchCustomerInfo,
      setShowMultipleUsersDialog,
      setMultipleUsersList
    );
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.searchCustomerContainer}>
        <div className={styles.inputContainer}>
          <Text>Please enter customer's registered mobile number.</Text>
          <PhoneWithIsdInput
            phone={phoneNumber}
            onPhoneChange={setPhoneNumber}
            phoneLabel="Enter a Phone number"
          />
        </div>
        <div className={styles.businessVertical}>
          <Text>Please select the business vertical</Text>
          <SingleSelect
            tag="Select business vertical"
            value={selectedBusinessVertical}
            menuItems={BUSINESS_VERTICAL_IDS}
            handleChange={(event) =>
              handleChangeBusinessVertical(event, setSelectedBusinessVertical)
            }
          />
        </div>
        <div className={styles.viewCustomerDetailsButton}>
          <PrimaryButton
            onClick={handleViewCustomerOrders}
            disabled={
              !canViewCustomerOrders(
                phoneNumber,
                selectedBusinessVertical,
                cashierPhoneNumber
              )
            }
          >
            {isCustomerInfoLoading ? <Loader /> : "View Customer Details"}
          </PrimaryButton>
        </div>
      </div>
      <Dialog
        open={showMultipleUsersDialog}
        handleClose={() => setShowMultipleUsersDialog(false)}
      >
        <MultipleUsers
          customerDetails={multipleUsersList}
          handleOnSelectUser={(uamId) => {
            setShowMultipleUsersDialog(false);
            navigate(
              buildCustomerOrdersPath({
                customerId: uamId,
                countryId: selectedCountry.countryId,
                isdCode: selectedCountry.isdCode,
              }),
            );
          }}
        />
      </Dialog>
    </div>
  );
};

export default HomePage;
